import { Fragment, type FC, type ReactNode, useEffect, useMemo, useState } from 'react'

import { Box, CollapsableCard, Divider, Text } from '@factorialco/deprecated-design-system'
import { Calendar, Clock, Money } from '@factorialco/f0-react/icons/app'

import { convertDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { buildAttendancePeriods } from './buildAttendancePeriods'
import styles from './ContractBreakdownCard.module.css'
import { SummaryRow } from './SummaryRow'
import type { AttendancePeriod, ContractBreakdownNode } from './types'

interface ContractBreakdownCardProps {
  contract: ContractBreakdownNode
  index: number
  locale: string
  formatMoney: (cents: number | null | undefined) => string
  formatMinutes: (minutes: number | null | undefined) => string
  defaultExpanded: boolean
  openVersion: number
}

const formatPeriodContribution = (
  period: AttendancePeriod,
  formatMinutes: (minutes: number | null | undefined) => string
) => {
  const payableMinutes = formatMinutes(period.totalPayableMinutes)

  return `${i18n.t('salary_advance.employee.breakdown_side_panel.contract.hourly.payable_label')}: ${payableMinutes}`
}

const frequencyLabel = (salaryFrequency: ContractBreakdownNode['salaryFrequency']) => {
  if (!salaryFrequency) return '-'

  switch (salaryFrequency) {
    case 'hourly':
      return i18n.t('salary_advance.employee.breakdown_side_panel.contract.frequency.hourly')
    case 'daily':
      return i18n.t('salary_advance.employee.breakdown_side_panel.contract.frequency.daily')
    case 'weekly':
      return i18n.t('salary_advance.employee.breakdown_side_panel.contract.frequency.weekly')
    case 'monthly':
      return i18n.t('salary_advance.employee.breakdown_side_panel.contract.frequency.monthly')
    case 'yearly':
      return i18n.t('salary_advance.employee.breakdown_side_panel.contract.frequency.yearly')
    default:
      return String(salaryFrequency)
  }
}

export const ContractBreakdownCard: FC<ContractBreakdownCardProps> = ({
  contract,
  locale,
  formatMoney,
  formatMinutes,
  defaultExpanded,
  openVersion,
  index,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded)
  useEffect(() => {
    setExpanded(defaultExpanded)
  }, [defaultExpanded, openVersion])
  const toggleExpanded = () => setExpanded((current) => !current)

  const frequencyLabelText = frequencyLabel(contract.salaryFrequency)
  const accrualMethodLabel =
    contract.strategy === 'hourly'
      ? i18n.t('salary_advance.employee.breakdown_side_panel.contract.accrual_via.time_tracking')
      : i18n.t('salary_advance.employee.breakdown_side_panel.contract.accrual_via.calendar')
  const StrategyIcon = contract.strategy === 'hourly' ? Clock : Calendar
  const attendancePeriods = useMemo(
    () =>
      buildAttendancePeriods(
        contract.attendanceSegments ?? [],
        contract.periodStart,
        contract.periodEnd
      ),
    [contract.attendanceSegments, contract.periodEnd, contract.periodStart]
  )
  const netContributionPercentageFormatter = useMemo(
    () =>
      new Intl.NumberFormat(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    [locale]
  )

  const attendancePeriodLabel = (period: AttendancePeriod) => {
    if (period.startDate === period.endDate) {
      return convertDate(period.startDate, 'backend', 'ui_dmy_long')
    }

    return `${convertDate(period.startDate, 'backend', 'ui_dmy_long')} – ${convertDate(period.endDate, 'backend', 'ui_dmy_long')}`
  }
  const periodDetailItems = (period: AttendancePeriod) =>
    [
      {
        label: i18n.t(
          'salary_advance.employee.breakdown_side_panel.contract.hourly.worked_minutes_short'
        ),
        value: formatMinutes(period.workedMinutes),
        visible: period.workedMinutes > 0,
      },
      {
        label: i18n.t(
          'salary_advance.employee.breakdown_side_panel.contract.hourly.paid_leave_minutes_short'
        ),
        value: formatMinutes(period.paidLeaveMinutes),
        visible: period.paidLeaveMinutes > 0,
      },
      {
        label: i18n.t(
          'salary_advance.employee.breakdown_side_panel.contract.hourly.bank_holiday_credit_short'
        ),
        value: formatMinutes(period.bankHolidayCreditMinutes),
        visible: period.bankHolidayCreditMinutes > 0,
      },
      {
        label: i18n.t(
          'salary_advance.employee.breakdown_side_panel.contract.hourly.premium_bonus_short'
        ),
        value: formatMinutes(period.premiumBonusMinutes),
        visible: period.premiumBonusMinutes > 0,
      },
    ].filter((item) => item.visible)
  const grossSummaryValue = `${formatMinutes(contract.totalPayableMinutes)} × ${formatMoney(contract.hourlyRateCents)} = ${formatMoney(contract.grossCents)}`
  const netRatioValue =
    contract.netRatio != null
      ? `${netContributionPercentageFormatter.format(contract.netRatio * 100)}%`
      : '-'
  const netContributionSummaryValue = `${netRatioValue} → ${formatMoney(contract.contributionCents)}`

  const netContributionTooltip: ReactNode = useMemo(() => {
    if (contract.netRatio == null) {
      return (
        <Text size="100" color="white" className={styles.tooltipText}>
          {i18n.t(
            'salary_advance.employee.breakdown_side_panel.contract.hourly.net_contribution_tooltip_missing_ratio'
          )}
        </Text>
      )
    }

    if (
      contract.ssRate == null ||
      contract.effectiveIrpfRate == null ||
      contract.ssDeductionCents == null ||
      contract.irpfDeductionCents == null
    ) {
      return (
        <Text size="100" color="white" className={styles.tooltipText}>
          {i18n.t(
            'salary_advance.employee.breakdown_side_panel.contract.hourly.net_contribution_tooltip',
            {
              gross: formatMoney(contract.grossCents),
              ratio: netRatioValue,
            }
          )}
        </Text>
      )
    }

    const { ssRate, effectiveIrpfRate, ssDeductionCents, irpfDeductionCents } = contract
    const ssRateFormatted = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(ssRate * 100)

    const irpfRateFormatted = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(effectiveIrpfRate * 100)

    const netPercentFormatted = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(contract.netRatio * 100)

    return (
      <Box flexDirection="column" gap="s4" className={styles.tooltipContent}>
        <Text size="100" weight="semibold" color="white" className={styles.tooltipHeader}>
          {i18n.t('salary_advance.employee.breakdown_side_panel.contract.hourly.tooltip_header')}
        </Text>
        <Box
          flexDirection="row"
          justifyContent="spaceBetween"
          alignItems="center"
          className={styles.tooltipRow}
        >
          <Text size="100" color="white">
            {i18n.t('salary_advance.employee.breakdown_side_panel.contract.hourly.tooltip_gross')}
          </Text>
          <Text size="100" color="white" className={styles.tooltipValue}>
            {formatMoney(contract.grossCents)}
          </Text>
        </Box>
        <Box
          flexDirection="row"
          justifyContent="spaceBetween"
          alignItems="center"
          className={`${styles.tooltipRow} ${styles.tooltipRowSpaced}`}
        >
          <Text size="100" color="white">
            {i18n.t('salary_advance.employee.breakdown_side_panel.contract.hourly.tooltip_ss')}
          </Text>
          <Text size="100" color="white" className={styles.tooltipValue}>
            {ssRateFormatted}% → −{formatMoney(ssDeductionCents)}
          </Text>
        </Box>
        <Box
          flexDirection="row"
          justifyContent="spaceBetween"
          alignItems="center"
          className={styles.tooltipRow}
        >
          <Text size="100" color="white">
            {i18n.t('salary_advance.employee.breakdown_side_panel.contract.hourly.tooltip_irpf')}
          </Text>
          <Text size="100" color="white" className={styles.tooltipValue}>
            {irpfRateFormatted}% → −{formatMoney(irpfDeductionCents)}
          </Text>
        </Box>
        <Box
          flexDirection="row"
          justifyContent="spaceBetween"
          alignItems="center"
          className={`${styles.tooltipRow} ${styles.tooltipRowSpaced}`}
        >
          <Text size="100" weight="semibold" color="white">
            {i18n.t(
              'salary_advance.employee.breakdown_side_panel.contract.hourly.tooltip_net_total'
            )}
          </Text>
          <Text size="100" weight="semibold" color="white" className={styles.tooltipValue}>
            {netPercentFormatted}% → {formatMoney(contract.contributionCents)}
          </Text>
        </Box>
      </Box>
    )
  }, [contract, formatMoney, netRatioValue, locale])
  const calendarDaysSuffix = i18n.t(
    'salary_advance.employee.breakdown_side_panel.contract.calendar.days_suffix'
  )
  const calendarProrationValue =
    contract.completedDays != null && contract.daysInMonth != null
      ? `${contract.completedDays} / ${contract.daysInMonth} ${calendarDaysSuffix}`
      : '-'
  const calendarUnpaidLeaveValue =
    contract.unpaidLeaveDays != null ? `${contract.unpaidLeaveDays} ${calendarDaysSuffix}` : '-'
  const hasCalendarFormulaData =
    contract.monthlyNetSalaryCents != null &&
    contract.completedDays != null &&
    contract.daysInMonth != null

  return (
    <CollapsableCard
      Card={
        <button
          type="button"
          onClick={toggleExpanded}
          aria-expanded={expanded}
          className="w-full cursor-pointer border-0 bg-transparent p-0 text-left"
        >
          <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center">
            <Box flexDirection="column" gap="s4">
              <Box flexDirection="row" alignItems="center" gap="s6">
                <Money width={14} height={14} />
                <Text size="300" weight="semibold" color="grey1000">
                  {`${i18n.t('salary_advance.employee.breakdown_side_panel.contract.title')} #${index + 1}`}
                </Text>
              </Box>
              <Box flexDirection="row" alignItems="center" gap="s4">
                <StrategyIcon width={12} height={12} />
                <Text size="100" color="grey600">
                  {frequencyLabelText} · {accrualMethodLabel} ·{' '}
                  {convertDate(contract.periodStart, 'backend', 'ui_dmy_long')} –{' '}
                  {convertDate(contract.periodEnd, 'backend', 'ui_dmy_long')}
                </Text>
              </Box>
            </Box>
            <Text size="300" weight="semibold" color="grey1000">
              {formatMoney(contract.contributionCents)}
            </Text>
          </Box>
        </button>
      }
      Content={
        <Box flexDirection="column" gap="s8">
          {contract.strategy === 'hourly' ? (
            <>
              <Box flexDirection="column" gap="s12">
                <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center" gap="s8">
                  <Box flexDirection="row" alignItems="center" gap="s6">
                    <Clock width={14} height={14} />
                    <Text size="200" weight="semibold" color="grey1000">
                      {i18n.t(
                        'salary_advance.employee.breakdown_side_panel.contract.hourly.approved_segments'
                      )}
                    </Text>
                  </Box>
                </Box>

                {attendancePeriods.length > 0 ? (
                  <>
                    {attendancePeriods.map((period, periodIndex) => {
                      const periodKey = `${period.startDate}-${period.endDate}-${periodIndex}`
                      const details = periodDetailItems(period)

                      return (
                        <Fragment key={periodKey}>
                          <Box
                            flexDirection="row"
                            justifyContent="spaceBetween"
                            gap="s16"
                            padding="s12"
                          >
                            <Box flexDirection="column" gap="s6" className="min-w-0">
                              <Text size="100" weight="semibold" color="grey700">
                                {attendancePeriodLabel(period)}
                              </Text>
                              {details.length > 0 ? (
                                <Box flexDirection="column" gap="s4">
                                  {details.map((detail) => (
                                    <Text
                                      key={`${periodKey}-${detail.label}`}
                                      size="100"
                                      color="grey600"
                                    >
                                      {detail.label}: {detail.value}
                                    </Text>
                                  ))}
                                </Box>
                              ) : (
                                <Text size="100" color="grey600">
                                  {i18n.t(
                                    'salary_advance.employee.breakdown_side_panel.contract.hourly.no_details'
                                  )}
                                </Text>
                              )}
                            </Box>

                            <Box flexDirection="column" alignItems="flexEnd">
                              <Text size="100" weight="semibold" color="grey1000">
                                {formatPeriodContribution(period, formatMinutes)}
                              </Text>
                            </Box>
                          </Box>
                          {periodIndex < attendancePeriods.length - 1 && <Divider />}
                        </Fragment>
                      )
                    })}
                  </>
                ) : (
                  <Text size="100" color="grey700">
                    {i18n.t(
                      'salary_advance.employee.breakdown_side_panel.contract.hourly.no_approved_periods'
                    )}
                  </Text>
                )}

                <Divider />

                <Box flexDirection="column" gap="s12" paddingTop="s4" paddingBottom="s4">
                  <SummaryRow
                    label={i18n.t(
                      'salary_advance.employee.breakdown_side_panel.contract.hourly.gross'
                    )}
                    value={grossSummaryValue}
                  />
                  <SummaryRow
                    label={i18n.t(
                      'salary_advance.employee.breakdown_side_panel.contract.hourly.net_contribution'
                    )}
                    value={netContributionSummaryValue}
                    bold
                    tooltip={netContributionTooltip}
                    tooltipClassName={styles.netContributionTooltip}
                  />
                </Box>
              </Box>
            </>
          ) : (
            <>
              <SummaryRow
                label={i18n.t(
                  'salary_advance.employee.breakdown_side_panel.contract.calendar.monthly_net_salary'
                )}
                value={formatMoney(contract.monthlyNetSalaryCents)}
              />
              <SummaryRow
                label={i18n.t(
                  'salary_advance.employee.breakdown_side_panel.contract.calendar.proration'
                )}
                value={calendarProrationValue}
              />
              <SummaryRow
                label={i18n.t(
                  'salary_advance.employee.breakdown_side_panel.contract.calendar.unpaid_leave'
                )}
                value={calendarUnpaidLeaveValue}
              />
              <Divider />
              <SummaryRow
                label={i18n.t(
                  'salary_advance.employee.breakdown_side_panel.contract.calendar.calendar_accrual'
                )}
                value={formatMoney(contract.contributionCents)}
                bold
              />
              {hasCalendarFormulaData && (
                <Text size="100" color="grey600">
                  {i18n.t(
                    'salary_advance.employee.breakdown_side_panel.contract.calendar.accrual_formula',
                    {
                      monthly_net_salary: formatMoney(contract.monthlyNetSalaryCents),
                      completed_days: contract.completedDays,
                      days_in_month: contract.daysInMonth,
                      calendar_accrual: formatMoney(contract.contributionCents),
                    }
                  )}
                </Text>
              )}
            </>
          )}
        </Box>
      }
      open={expanded}
    />
  )
}
